import { CSRF_COOKIE, CSRF_HEADER, DEVICE_HEADER } from "@mandrac/shared";

export interface ApiIssue {
  path: string;
  message: string;
  params?: Record<string, string | number>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: ApiIssue[],
    public requestId?: string,
    /** Seconds from the `retry-after` header (sent with 429). */
    public retryAfter?: number,
  ) {
    super(message);
  }
  get isNetwork() {
    return this.status === 0;
  }
}

export function readCookie(name: string): string | null {
  const m = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return m ? decodeURIComponent(m.slice(name.length + 1)) : null;
}

const DEVICE_TOKEN_KEY = "mandrac.deviceToken";
export function getDeviceToken(): string | null {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setDeviceToken(token: string | null) {
  try {
    if (token) localStorage.setItem(DEVICE_TOKEN_KEY, token);
    else localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  raw?: boolean;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined ? "POST" : "GET");
  const url = new URL(`/api${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
  const headers: Record<string, string> = { accept: "application/json" };
  const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers["content-type"] = "application/json";
  if (method !== "GET") {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  // The office never acts as a dock device, even in a browser that is one.
  if (window.location.pathname.startsWith("/dock")) {
    const token = getDeviceToken();
    if (token) headers[DEVICE_HEADER] = token;
  }
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: "same-origin",
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch {
    throw new ApiError(0, "network", "error.network");
  }
  if (opts.raw && res.ok) return res as unknown as T;
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const e = (data as { error?: { code: string; message: string; details?: ApiIssue[]; requestId?: string } } | null)?.error;
    const retryAfter = Number(res.headers.get("retry-after"));
    throw new ApiError(
      res.status,
      e?.code ?? "http",
      e?.message ?? "error.generic",
      e?.details,
      e?.requestId ?? res.headers.get("x-request-id") ?? undefined,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Map API issues to `{ fieldPath: i18n message }` for forms. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !err.details) return {};
  const out: Record<string, string> = {};
  for (const d of err.details) if (!(d.path in out)) out[d.path] = d.message;
  return out;
}
